import { Injectable } from "@angular/core";
import { BehaviorSubject, Observable, of, timer } from "rxjs";
import { shareReplay, switchMap, tap, catchError } from "rxjs/operators";

interface CacheEntry<T> {
  data: BehaviorSubject<T | null>;
  observable: Observable<T>;
  timestamp: number;
  loading: boolean;
}

@Injectable({
  providedIn: "root",
})
export class CacheService {
  private cache = new Map<string, CacheEntry<any>>();
  private readonly CACHE_DURATION = 15 * 60 * 1000; // 15 minuti

  constructor() {}

  /**
   * Ottiene dati dalla cache o esegue la chiamata HTTP se necessario
   */
  get<T>(
    key: string,
    httpCall: () => Observable<T>,
    ttl: number = this.CACHE_DURATION
  ): Observable<T> {
    const now = Date.now();
    const cached = this.cache.get(key);

    // Se i dati sono in cache e ancora validi, restituiscili
    if (cached && !this.isExpired(cached.timestamp, ttl)) {
      const currentData = cached.data.getValue();
      if (currentData !== null) {
        return of(currentData);
      }
    }

    // Se c'è già una chiamata in corso, restituisci l'observable esistente
    if (cached?.loading) {
      return cached.observable;
    }

    // Crea una nuova entry di cache
    const dataSubject = new BehaviorSubject<T | null>(
      cached?.data.getValue() || null
    );

    const observable = httpCall().pipe(
      tap((data) => {
        // Aggiorna la cache con i nuovi dati
        this.updateCache(key, data, now, dataSubject);
      }),
      catchError((error) => {
        // In caso di errore, mantieni i dati vecchi se disponibili
        const oldData = dataSubject.getValue();
        if (oldData !== null) {
          return of(oldData);
        }
        throw error;
      }),
      shareReplay(1)
    );

    // Memorizza l'entry di cache
    this.cache.set(key, {
      data: dataSubject,
      observable,
      timestamp: now,
      loading: true,
    });

    return observable;
  }

  /**
   * Invalida una specifica entry di cache
   */
  invalidate(key: string): void {
    this.cache.delete(key);
  }

  /**
   * Invalida tutte le entry di cache che iniziano con il prefisso
   */
  invalidateByPrefix(prefix: string): void {
    const keysToDelete = Array.from(this.cache.keys()).filter((key) =>
      key.startsWith(prefix)
    );
    keysToDelete.forEach((key) => this.cache.delete(key));
  }

  /**
   * Svuota completamente la cache
   */
  clear(): void {
    this.cache.clear();
  }

  /**
   * Ottiene i dati dall'ultima chiamata (se disponibili) senza fare nuove chiamate HTTP
   */
  getFromCacheOnly<T>(key: string): T | null {
    const cached = this.cache.get(key);
    return cached?.data.getValue() || null;
  }

  /**
   * Forza il refresh dei dati invalidando la cache e facendo una nuova chiamata
   */
  refresh<T>(key: string, httpCall: () => Observable<T>): Observable<T> {
    this.invalidate(key);
    return this.get(key, httpCall);
  }

  private updateCache<T>(
    key: string,
    data: T,
    timestamp: number,
    dataSubject: BehaviorSubject<T | null>
  ): void {
    dataSubject.next(data);
    const cached = this.cache.get(key);
    if (cached) {
      cached.timestamp = timestamp;
      cached.loading = false;
    }
  }

  private isExpired(timestamp: number, ttl: number): boolean {
    return Date.now() - timestamp > ttl;
  }

  /**
   * Crea una cache key dai parametri
   */
  createKey(baseKey: string, ...params: (string | number | boolean)[]): string {
    const paramString = params.map((p) => String(p)).join("|");
    return `${baseKey}:${paramString}`;
  }
}
